import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Box, Button, CircularProgress, Divider, Paper, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import InputField from "components/Input/InputField";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchTermLockInfo } from "views/admin/Accounts/admissionSync";
import { patchApplicationSection } from "./AddAdmissions/admissionApi";
import {
  displayNetAmount,
  validateOfferReceivedFees,
} from "./AddAdmissions/admissionFeeValidation";
import TermScheduleTable from "./AddAdmissions/applicationFlow/TermScheduleTable";
import { relockSavedSchedule } from "./AddAdmissions/applicationFlow/superAdminTermEdit";
import CollegeStandardFeesReference from "./AddAdmissions/CollegeStandardFeesReference";
import { resolvePlacementSelection } from "./AddAdmissions/placementSelection";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";

const parseApplicationData = (jsonStr) => {
  if (!jsonStr) return {};
  try {
    return JSON.parse(jsonStr);
  } catch {
    return {};
  }
};

export default function AdmissionFeeDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [{ courseList, uniData }, { refetchStudent, refetchCourseList }] = useKothar();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [generalInfo, setGeneralInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lockedTermIds, setLockedTermIds] = useState(() => new Set());
  const [rejectedTermIndices, setRejectedTermIndices] = useState(() => new Set());

  useEffect(() => {
    if (!courseList?.length) refetchCourseList();
  }, [courseList?.length, refetchCourseList]);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    setLoading(true);

    axios
      .get(`${API_URL}/student/${id}`)
      .then(({ data }) => {
        if (cancelled) return;
        const value = data?.data ?? data;
        const appData = parseApplicationData(value?.applicationData);
        const merged = { ...value, ...appData };
        const { selectedUniversity } = resolvePlacementSelection(courseList, merged);
        setGeneralInfo({
          ...merged,
          selectedUniversity: selectedUniversity ?? merged.selectedUniversity,
        });
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err?.response?.data?.errorMessage || "Unable to load fee details");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, courseList]);

  const refreshLockInfo = useCallback(() => {
    if (!generalInfo?.clientId || !generalInfo?.id) return;
    fetchTermLockInfo(generalInfo.clientId, generalInfo.id).then(
      ({ lockedIds, rejectedIndices }) => {
        setLockedTermIds(lockedIds);
        setRejectedTermIndices(rejectedIndices);
      }
    );
  }, [generalInfo?.clientId, generalInfo?.id]);

  useEffect(() => {
    if (!generalInfo?.clientId || !generalInfo?.id) {
      setLockedTermIds(new Set());
      setRejectedTermIndices(new Set());
      return;
    }
    let cancelled = false;
    fetchTermLockInfo(generalInfo.clientId, generalInfo.id).then(
      ({ lockedIds, rejectedIndices }) => {
        if (!cancelled) {
          setLockedTermIds(lockedIds);
          setRejectedTermIndices(rejectedIndices);
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [generalInfo?.clientId, generalInfo?.id]);

  const applicationWithdrawn = Boolean(generalInfo?.applicationWithdrawn);

  const displayFees = useMemo(() => {
    const uni = generalInfo?.selectedUniversity || {};
    return {
      feePerSem: uni.feePerSem ?? generalInfo?.fee ?? "",
      scholarship: uni.scholarship ?? "",
      noOfSem: uni.noOfSem ?? generalInfo?.term ?? "",
      totalFees: uni.totalFees ?? "",
    };
  }, [generalInfo?.selectedUniversity, generalInfo?.fee, generalInfo?.term]);

  const courseName = courseList?.find((c) => c.courseId === generalInfo?.course)?.courseName || "—";
  const universityName =
    uniData?.find((u) => u.id === generalInfo?.university)?.name ||
    uniData?.find((u) => u.id === generalInfo?.university)?.universityName ||
    generalInfo?.selectedUniversity?.universityName ||
    "—";

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setGeneralInfo((prev) => ({ ...prev, [name]: value }));
  };

  const saveTermScheduleForTask = async () => {
    if (!id) return false;
    if (
      !generalInfo?.studentId?.trim() ||
      !generalInfo?.courseStartDate ||
      !generalInfo?.courseEndDate
    ) {
      toast.error(
        "Offer received details must be saved in admission before sending for verification."
      );
      return false;
    }
    const feeError = validateOfferReceivedFees(generalInfo);
    if (feeError) {
      toast.error(feeError);
      return false;
    }
    try {
      await patchApplicationSection(id, "offer-received", {
        studentId: generalInfo.studentId,
        courseStartDate: generalInfo.courseStartDate,
        courseEndDate: generalInfo.courseEndDate,
        term: generalInfo.term,
        termSchedule: generalInfo.termSchedule,
      });
      setGeneralInfo(relockSavedSchedule);
      return true;
    } catch (err) {
      toast.error(
        err?.response?.data?.message ??
          err?.response?.data?.errorMessage ??
          "Failed to save. Please try again."
      );
      return false;
    }
  };

  const handleSave = async () => {
    if (!id) return;

    const feeError = validateOfferReceivedFees(generalInfo);
    if (feeError) {
      toast.error(feeError);
      return;
    }

    setSaving(true);
    try {
      const res = await patchApplicationSection(id, "offer-received", {
        offerReceivedDocs: generalInfo?.offerReceivedDocs ?? [],
        studentId: generalInfo?.studentId,
        agentName: generalInfo?.agentName,
        courseStartDate: generalInfo?.courseStartDate,
        courseEndDate: generalInfo?.courseEndDate,
        agreedFeePerSemester: generalInfo?.agreedFeePerSemester,
        agreedDiscount: generalInfo?.agreedDiscount,
        agreedFeePerUnit: generalInfo?.agreedFeePerUnit,
        agreedDiscountPerUnit: generalInfo?.agreedDiscountPerUnit,
        term: generalInfo?.term,
        termSchedule: generalInfo?.termSchedule,
      });
      setGeneralInfo(relockSavedSchedule);
      const cadId = res?.clientAccountDetailId ?? res?.data?.clientAccountDetailId;
      if (cadId) {
        setGeneralInfo((prev) => ({ ...prev, clientAccountDetailId: cadId }));
      }
      const savedCadId = cadId ?? generalInfo?.clientAccountDetailId;
      if (savedCadId) {
        await queryClient.invalidateQueries({ queryKey: ["admissionTerms", savedCadId] });
      }
      toast.success("Fee details saved.");
      refetchStudent?.();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ??
          err?.response?.data?.errorMessage ??
          "Failed to save fee details."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <Box
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.default",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/admin/admission")}
                size="small"
              >
                Back
              </Button>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Fee details — {generalInfo?.name || state?.item?.name || "Admission"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {universityName} · {courseName}
                </Typography>
              </Box>
            </Box>
            <div className="flex items-center gap-2">
              <Button
                variant="contained"
                startIcon={<VisibilityOutlined />}
                onClick={() => navigate(`/admin/admission/edit/${id}`)}
                disabled={!generalInfo || saving}
              >
                View Admission Details
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveOutlinedIcon />}
                onClick={handleSave}
                disabled={!generalInfo || saving || applicationWithdrawn}
              >
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </Box>

          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : !generalInfo ? (
              <Typography color="text.secondary">No admission data available.</Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <CollegeStandardFeesReference displayFees={displayFees} />

                <div>
                  <Typography variant="h6" fontWeight={700}>
                    Fee details{" "}
                    <Typography component="span" variant="caption" color="text.secondary">
                      — negotiated / agreed fees for this student
                    </Typography>
                  </Typography>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                    <InputField
                      label="Term Fee"
                      name="agreedFeePerSemester"
                      disabled={applicationWithdrawn}
                      type="number"
                      min={0}
                      value={generalInfo?.agreedFeePerSemester ?? ""}
                      onChange={handleInputChange}
                    />
                    <InputField
                      label="Discount/Term"
                      name="agreedDiscount"
                      type="number"
                      disabled={applicationWithdrawn}
                      min={0}
                      value={generalInfo?.agreedDiscount ?? ""}
                      onChange={handleInputChange}
                    />
                    <InputField
                      label="Agreed Fee/Term"
                      name="agreedFeePerTerm"
                      type="number"
                      min={0}
                      value={displayNetAmount(
                        generalInfo?.agreedFeePerSemester,
                        generalInfo?.agreedDiscount
                      )}
                      disabled
                    />
                    <InputField
                      label="Fee/Unit"
                      name="agreedFeePerUnit"
                      type="number"
                      disabled={applicationWithdrawn}
                      min={0}
                      value={generalInfo?.agreedFeePerUnit ?? ""}
                      onChange={handleInputChange}
                    />
                    <InputField
                      label="Discount/Unit"
                      name="agreedDiscountPerUnit"
                      type="number"
                      disabled={applicationWithdrawn}
                      min={0}
                      value={generalInfo?.agreedDiscountPerUnit ?? ""}
                      onChange={handleInputChange}
                    />
                    <InputField
                      label="Agreed Fee/Unit"
                      name="agreedFeePerUnitNet"
                      type="number"
                      min={0}
                      value={displayNetAmount(
                        generalInfo?.agreedFeePerUnit,
                        generalInfo?.agreedDiscountPerUnit
                      )}
                      disabled
                    />
                  </div>
                </div>

                <Divider />

                <TermScheduleTable
                  generalInfo={generalInfo}
                  setGeneralInfo={setGeneralInfo}
                  lockedTermIds={lockedTermIds}
                  rejectedTermIndices={rejectedTermIndices}
                  onSaveBeforeTask={saveTermScheduleForTask}
                  onSaveTermSchedule={saveTermScheduleForTask}
                  onRefreshLock={refreshLockInfo}
                  scheduleReadOnly={applicationWithdrawn}
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
                  <Button variant="outlined" onClick={() => navigate("/admin/admission")}>
                    Back to list
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveOutlinedIcon />}
                    onClick={handleSave}
                    disabled={saving || applicationWithdrawn}
                  >
                    {saving ? "Saving…" : "Save changes"}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </div>
    </div>
  );
}
